import gql from 'graphql-tag';
import { apolloClient } from '@/apollo/apollo';

import * as actions from '@/constants/actions';
import * as appColors from '@/functions/appColors';

export default {
    async mounted() {
        this.initialize();
    },
    data() {
        return {
            properties: [],
            queryData: {},
            queryDone: true,
            temperatureDatasets: null,
            precipitationDatasets: null,
            snowDatasets: null,
            SWEDatasets: null,
            MSSDatasets: null,
            dataQuery: gql`query stationClimate($id: Int!) {
                getStation(id: $id) {
                    name
                    nid
                    net
                    yr
                    ty
                    description
                    licence_link
                    elevation
                    temperature {
                        current
                        historical
                    }
                    precipitation{
                        current
                        historical
                    }
                    snow_on_ground_depth {
                        current
                        historical
                    }
                    snow_water_equivalent {
                        current
                        historical
                    }
                    manual_snow_survey {
                        current
                        historical
                    }
                }
            }`,
        };
    },
    methods: {
        async initialize() {
            this.queryDone = false;
            this.queryData = {};

            this.temperatureDatasets = null;
            this.precipitationDatasets = null;
            this.snowDatasets = null;

            // fetch data
            await this.getData();
            this.buildProps();
        },
        async getData() {
            if (!this.station) return;
            this.$store.dispatch(actions.ADD_LOADING, 'load station data');
            try {
                const { data } = await apolloClient.query({
                    query: this.dataQuery,
                    variables: { id: this.station.id },
                });
                // populate variables
                this.buildDatasets(data.getStation);
                this.queryData = data.getStation;

                this.queryDone = true;
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error retrieving station data');
            } finally {
                this.$store.dispatch(actions.REMOVE_LOADING, 'load station data');
            }
        },
        buildDatasets(data) {
            // reduce number of data points to plot on the spark line
            const temperatureData = data.temperature.historical.filter((v, i) => i % 10 === 0);

            this.temperatureDatasets = [
                {
                    label: 'Max Avg. Temperature',
                    data: temperatureData.map(d => d.maxavg),
                    // backgroundColor: sparklineBgColor,
                    borderColor: appColors.sparklineColor2,
                },
                {
                    label: 'Min Avg. Temperature',
                    data: temperatureData.map(d => d.minavg),
                    borderColor: appColors.sparklineColor2,
                    fill: '-1',
                },
                {
                    label: 'Max P90',
                    data: temperatureData.map(d => d.maxp90),
                    borderColor: appColors.sparklineColor1,
                },
                {
                    label: 'Min P10',
                    data: temperatureData.map(d => d.minp10),
                    borderColor: appColors.sparklineColor1,
                    backgroundColor: appColors.sparklineBgColor,
                    fill: '-1',
                },
            ];

            this.precipitationDatasets = [
                {
                    label: '10th %ile',
                    data: data.precipitation.historical.map(d => d.p10),
                    borderColor: appColors.sparklineColor1,
                },
                {
                    label: '90th %ile',
                    data: data.precipitation.historical.map(d => d.p90),
                    borderColor: appColors.sparklineColor1,
                    fill: '-1',
                },
                {
                    label: '25th %ile',
                    data: data.precipitation.historical.map(d => d.p25),
                    backgroundColor: appColors.sparklineBgColor,
                    borderColor: appColors.sparklineColor2,
                },
                {
                    label: '75th %ile',
                    data: data.precipitation.historical.map(d => d.p75),
                    borderColor: appColors.sparklineColor2,
                    fill: '-1',
                },
            ];

            const snowData = data.snow_on_ground_depth.historical.filter((v, i) => i % 10 === 0);

            this.snowDatasets = [
                {
                    label: '25th %ile',
                    data: snowData.map(d => d.p25),
                    borderColor: appColors.sparklineColor2,
                },
                {
                    label: '75th %ile',
                    data: snowData.map(d => d.p75),
                    borderColor: appColors.sparklineColor2,
                    backgroundColor: appColors.sparklineBgColor,
                    fill: '-1',
                },
                {
                    label: '10th %ile',
                    data: snowData.map(d => d.p10),
                    borderColor: appColors.sparklineColor1,
                },
                {
                    label: '90th %ile',
                    data: snowData.map(d => d.p90),
                    borderColor: appColors.sparklineColor1,
                    backgroundColor: appColors.sparklineBgColor,
                    fill: '-1',
                },
            ];

            const SWEData = data.snow_water_equivalent.historical.filter((v, i) => i % 10 === 0);

            this.SWEDatasets = [
                {
                    label: '25th %ile',
                    data: SWEData.map(d => d.p25),
                    borderColor: appColors.sparklineColor2,
                    backgroundColor: appColors.sparklineBgColor,
                    fill: '-1',
                },
                {
                    label: '75th %ile',
                    data: SWEData.map(d => d.p75),
                    borderColor: appColors.sparklineColor2,
                },
                {
                    label: '10th %ile',
                    data: SWEData.map(d => d.p10),
                    borderColor: appColors.sparklineColor1,
                },
                {
                    label: '90th %ile',
                    data: SWEData.map(d => d.p90),
                    borderColor: appColors.sparklineColor1,
                    backgroundColor: appColors.sparklineBgColor,
                    fill: '-1',
                },
            ];
            const MSSData = data.manual_snow_survey.historical.filter((v, i) => i % 10 === 0);

            this.MSSDatasets = [
                {
                    label: '25th %ile',
                    data: MSSData.map(d => d.p25),
                    borderColor: appColors.sparklineColor2,
                    backgroundColor: appColors.sparklineBgColor,
                    fill: '-1',
                },
                {
                    label: '75th %ile',
                    data: MSSData.map(d => d.p75),
                    borderColor: appColors.sparklineColor2,
                },
                {
                    label: '10th %ile',
                    data: MSSData.map(d => d.p10),
                    borderColor: appColors.sparklineColor1,
                },
                {
                    label: '90th %ile',
                    data: MSSData.map(d => d.p90),
                    borderColor: appColors.sparklineColor1,
                    backgroundColor: appColors.sparklineBgColor,
                    fill: '-1',
                },
            ];
        },
        /**
         * build an array of properties to display
         */
        buildProps() {
            const data = this.queryData;
            const properties = [];

            properties.push({
                title: 'Description',
                value: data.description,
            });

            properties.push({
                title: 'Date Range',
                value: `${this.years[0]}-${this.years[1]}`,
            });

            if (data.elevation) {
                properties.push({
                    title: 'Elevation',
                    value: `${data.elevation}m`,
                });
            }

            properties.push({
                title: 'Status',
                value: data.ty,
            });

            this.properties = properties;
        },
    },
    computed: {
        station() {
            return this.$store.state.map.selectedStation;
        },
        name() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.name;
        },
        networkUrl() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.licence_link;
        },
        network() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.net;
        },
        networkId() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.nid;
        },
        type() {
            if (!this.queryDone) {
                return '';
            }
            return this.queryData.ty;
        },
        years() {
            if (!this.queryDone) {
                return [];
            }
            return this.queryData.yr;
        },
        numYears() {
            if (this.years.length === 2) {
                const years = this.years[1] - this.years[0];
                return years + 1;
            }
            return 0;
        },
        precipitationData() {
            if (!this.queryDone) {
                return {};
            }
            return this.queryData.precipitation;
        },
        temperatureData() {
            if (!this.queryDone) {
                return {};
            }
            return this.queryData.temperature;
        },
        snowData() {
            if (!this.queryDone) {
                return {};
            }
            return this.queryData.snow_on_ground_depth;
        },
    },
    watch: {
        station() {
            // re-initialize component when a new station is selected
            this.initialize();
        },
    },
};



// WEBPACK FOOTER //
// ./src/mixins/climateData.js